import { v4 as uuidv4 } from "uuid";
import * as utils from "common/utils";
import actionTypes from "./_actions";
import * as _utils from "./_utils";
import { requiresOutdoorPigLiquidApplications, requiresOutdoorPigSolidApplications, requiresOutdoorPigStrawCompostApplications } from "common/viewModels";

const _saveDistributions = (offlineFarms, payload) => {
    const { farmId, budgetId, animalDistribution } = payload;

    return offlineFarms.map((offlineFarm) => {
        if (offlineFarm.id !== farmId) return offlineFarm;

        return {
            ...offlineFarm,
            farm: {
                ...offlineFarm.farm,
                offlineBudgets: offlineFarm.farm.offlineBudgets.map((offlineBudget) => {
                    if (offlineBudget.id !== budgetId) return offlineBudget;

                    const selectedEnterpriseTypes = [];
                    animalDistribution.distributions.forEach((distribution) => {
                        distribution.animals.forEach((animal) => {
                            if (selectedEnterpriseTypes.includes(animal.enterpriseType) === false) selectedEnterpriseTypes.push(animal.enterpriseType);
                        });
                    });
                    if (animalDistribution.hasOutdoorPigs) selectedEnterpriseTypes.push("OutdoorPigs");

                    let enterprises = offlineBudget.budget.enterprises.filter((enterprise) => selectedEnterpriseTypes.includes(enterprise.type));
                    const blocks = offlineBudget.budget.blocks.map((block) => {
                        if (!_utils.canDistributeAnimals(block)) return { ...block, animals: [] };

                        const distribution = animalDistribution.distributions.find((d) => d.blockId === block.id);
                        if (!distribution) return { ...block, animals: [] };

                        distribution.animals = distribution.animals.map((animal) => {
                            let enterprise = enterprises.find((e) => e.type === animal.enterpriseType);
                            if (!enterprise) {
                                enterprise = { id: uuidv4(), type: animal.enterpriseType, mobs: [] };
                                enterprises = enterprises.concat(enterprise);
                            }

                            return {
                                months: animal.months,
                                enterpriseId: enterprise.id,
                                percenBlockPastureProd: _utils.showPercenBlockPastureProd(animalDistribution.blockStockRatios) ? utils.round(animal.percenBlockPastureProd, 0) : null,
                                hasStreamAccess: animal.hasStreamAccess,
                                isFinishing: animal.isFinishing,
                                isWallows: animal.isWallows,
                                isPaceFenceline: animal.isPaceFenceline,
                                isMerino: animal.isMerino,
                            };
                        });

                        return {
                            ...block,
                            relativeProductivity: _utils.showRelativeProductivity(animalDistribution.relProdMethod) ? utils.round(distribution.relativeProductivity, 2) : 1,
                            animals: distribution.animals,
                        };
                    });

                    let structures = undefined;
                    if (offlineBudget.budget.structures) {
                        structures = offlineBudget.budget.structures.map((s) => {
                            return enterprises.find((e) => e.id === s.enterpriseId) ? s : { ...s, ...{ enterpriseId: undefined, animals: undefined } };
                        });
                    }

                    if (offlineBudget.budget.effluentSystem && !enterprises.some((e) => ["Dairy", "DairyGoat"].includes(e.type))) {
                        offlineBudget.budget.effluentSystem = undefined;
                    }

                    // Clean up associated feed supplements.
                    if (offlineBudget.budget.feedSupplements && offlineBudget.budget.feedSupplements.length > 0) {
                        const enterpriseIds = enterprises.map((e) => e.id);
                        const supplements = offlineBudget.budget.feedSupplements.reduce((results, supplement) => {
                            const destinations = (supplement.destinations || []).filter((destination) => destination.type !== "Enterprise" || enterpriseIds.includes(destination.enterpriseId));
                            results.push({ ...supplement, destinations });
                            return results;
                        }, []);
                        offlineBudget.budget.feedSupplements = supplements;
                    }

                    // Clean up dairy goat left overs
                    // Make sure any enterprises newly distributed/removed to/from pasture blocks are added/removed to/from specified left over feeding.
                    const dairyGoat = enterprises.find((e) => e.type === "DairyGoat");
                    if (dairyGoat && dairyGoat.dairyGoatLeftOvers) {
                        const nonDairyGoatEnterprisesOnPastureBlocks = _utils.getNonDairyGoatEnterpriesesOnPastureBlocks(enterprises, blocks);
                        if (dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers) {
                            nonDairyGoatEnterprisesOnPastureBlocks.forEach((ent) => {
                                if (!dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers.enterprisePercentages.some((ep) => ep.enterpriseId === ent.id)) {
                                    dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers.enterprisePercentages.push({ enterpriseId: ent.id, percentage: 0 });
                                }
                            });

                            const enterprisePercentages = dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers.enterprisePercentages.filter((ep) => {
                                return nonDairyGoatEnterprisesOnPastureBlocks.some((e) => e.id === ep.enterpriseId);
                            });
                            if (enterprisePercentages.length === 0) {
                                delete dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers;
                            } else {
                                dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers.enterprisePercentages = enterprisePercentages;
                            }
                        }
                        if (dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers && dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.fate === "FedOnPastoralBlocks") {
                            nonDairyGoatEnterprisesOnPastureBlocks.forEach((ent) => {
                                if (!dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages.some((ep) => ep.enterpriseId === ent.id)) {
                                    dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages.push({ enterpriseId: ent.id, percentage: 0 });
                                }
                            });

                            const enterprisePercentages = dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages.filter((ep) => {
                                return nonDairyGoatEnterprisesOnPastureBlocks.some((e) => e.id === ep.enterpriseId);
                            });
                            if (enterprisePercentages.length === 0) {
                                delete dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers;
                            } else {
                                dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages = enterprisePercentages;
                            }
                        }
                    }

                    return {
                        ...offlineBudget,
                        isDirty: true,
                        budget: {
                            ...offlineBudget.budget,
                            animalDistribution: { relProdMethod: animalDistribution.relProdMethod, blockStockRatios: animalDistribution.blockStockRatios },
                            enterprises: enterprises,
                            blocks: blocks,
                            structures: structures,
                        },
                    };
                }),
            },
        };
    });
};

const _saveEnterprise = (offlineFarms, payload) => {
    const { farmId, budgetId, enterprise } = payload;

    if (!enterprise.id) enterprise.id = uuidv4();

    return offlineFarms.map((offlineFarm) => {
        if (offlineFarm.id !== farmId) return offlineFarm;

        return {
            ...offlineFarm,
            farm: {
                ...offlineFarm.farm,
                offlineBudgets: offlineFarm.farm.offlineBudgets.map((offlineBudget) => {
                    if (offlineBudget.id !== budgetId) return offlineBudget;

                    const enterprises = (offlineBudget.budget.enterprises || []).map((e) => {
                        return e.id === enterprise.id ? enterprise : e;
                    });
                    if (!enterprises.some((e) => e.id === enterprise.id)) {
                        enterprises.push(enterprise);
                        // Must be a new enterprise. Add it to any dairy goat left overs.
                        const dairyGoat = enterprises.find((e) => e.type === "DairyGoat");
                        if (dairyGoat && dairyGoat.dairyGoatLeftOvers) {
                            enterprises.forEach((ent) => {
                                if (ent.id !== dairyGoat.id) {
                                    if (dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers) {
                                        if (!dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers.enterprisePercentages.some((ep) => ep.enterpriseId === ent.id)) {
                                            dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers.enterprisePercentages.push({ enterpriseId: ent.id, percentage: 0 });
                                        }
                                    }

                                    if (dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers) {
                                        if (dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.fate === "FedOnPastoralBlocks" && !dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages.some((ep) => ep.enterpriseId === ent.id)) {
                                            dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages.push({ enterpriseId: ent.id, percentage: 0 });
                                        }
                                    }
                                }
                            });
                        }
                        // Add an outdoor pig hut if enterprise is outdoor pig
                        if (enterprise.type === "OutdoorPigs") {
                            offlineBudget.budget.structures.push({ id: uuidv4(), type: "OutdoorPigHuts", enterpriseId: enterprise.id });
                        }
                    }

                    // Clean up outdoor pig structures
                    if (enterprise.type === "OutdoorPigs") {
                        if (enterprise.outdoorPigs.growOutUnitOnly) offlineBudget.budget.structures = offlineBudget.budget.structures.filter((s) => s.type !== "OutdoorPigFarrowingVillages");

                        if (offlineBudget.budget.outdoorPigEffluentSystem) {
                            if (!offlineBudget.budget.structures.some((s) => requiresOutdoorPigLiquidApplications(s))) delete offlineBudget.budget.outdoorPigEffluentSystem.liquidEffluentApplications;

                            if (!offlineBudget.budget.structures.some((s) => requiresOutdoorPigSolidApplications(s))) delete offlineBudget.budget.outdoorPigEffluentSystem.pondSolidApplications;

                            if (!offlineBudget.budget.structures.some((s) => requiresOutdoorPigStrawCompostApplications(s))) delete offlineBudget.budget.outdoorPigEffluentSystem.strawCompostApplications;
                        }
                    }

                    const animalDistribution = {
                        ...offlineBudget.budget.animalDistribution,
                    };

                    const blocks = enterprise.isNew
                        ? (offlineBudget.budget.blocks || []).map((block) => {
                              if (_utils.canDistributeAnimals(block)) {
                                  let animals = block.animals || [];
                                  if (!animals.some((a) => a.enterpriseId === enterprise.id)) {
                                      animals = animals.concat({
                                          enterpriseId: enterprise.id,
                                          months: [],
                                      });
                                  }
                                  return {
                                      ...block,
                                      relativeProductivity: animalDistribution.relProdMethod ? block.relativeProductivity : 1,
                                      animals,
                                  };
                              } else {
                                  return {
                                      ...block,
                                      animals: [],
                                  };
                              }
                          })
                        : offlineBudget.budget.blocks;

                    return {
                        ...offlineBudget,
                        isDirty: true,
                        budget: {
                            ...offlineBudget.budget,
                            animalDistribution: {
                                ...animalDistribution,
                                relProdMethod: animalDistribution.relProdMethod || 0,
                            },
                            blocks,
                            enterprises,
                        },
                    };
                }),
            },
        };
    });
};

const _deleteEnterprise = (offlineFarms, payload) => {
    const { farmId, budgetId, id } = payload;

    return offlineFarms.map((offlineFarm) => {
        if (offlineFarm.id !== farmId) return offlineFarm;

        return {
            ...offlineFarm,
            farm: {
                ...offlineFarm.farm,
                offlineBudgets: offlineFarm.farm.offlineBudgets.map((offlineBudget) => {
                    if (offlineBudget.id !== budgetId) return offlineBudget;

                    let enterprises = offlineBudget.budget.enterprises || [];

                    // If dairy being deleted, delete dairy replacements too.
                    const idsToBeDeleted = [id];
                    const enterpriseBeingDeleted = enterprises.find((e) => e.id === id);
                    if (enterpriseBeingDeleted && enterpriseBeingDeleted.type === "Dairy") {
                        const dairyReplacement = enterprises.find((e) => e.type === "DairyReplacements");
                        if (dairyReplacement) idsToBeDeleted.push(dairyReplacement.id);
                    }

                    enterprises = enterprises.filter((e) => !idsToBeDeleted.includes(e.id));

                    // Clean up associated dairy goat left overs.
                    const dairyGoat = enterprises.find((e) => e.type === "DairyGoat");
                    if (dairyGoat && dairyGoat.dairyGoatLeftOvers) {
                        if (dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers) {
                            const enterprisePercentages = dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers.enterprisePercentages.filter((ep) => !idsToBeDeleted.includes(ep.enterpriseId));
                            if (enterprisePercentages.length === 0) {
                                delete dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers;
                            } else {
                                dairyGoat.dairyGoatLeftOvers.pastureBlockLeftOvers.enterprisePercentages = enterprisePercentages;
                            }
                        }

                        if (dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers && dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.fate === "FedOnPastoralBlocks") {
                            const enterprisePercentages = dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages.filter((ep) => !idsToBeDeleted.includes(ep.enterpriseId));
                            if (enterprisePercentages.length === 0) {
                                delete dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers;
                            } else {
                                dairyGoat.dairyGoatLeftOvers.winteringPadLeftOvers.enterprisePercentages = enterprisePercentages;
                            }
                        }
                    }

                    //Removed animal grazing references
                    const filterAnimalGrazing = (parent) => {
                        if (parent.animalGrazing && parent.animalGrazing.percentageGrazed) {
                            parent.animalGrazing.percentageGrazed = parent.animalGrazing.percentageGrazed.filter((a) => !idsToBeDeleted.includes(a.enterpriseId));
                            const { length } = parent.animalGrazing.percentageGrazed;
                            if (length === 0) {
                                //No enterprises exist so delete the animal grazing
                                delete parent.animalGrazing;
                            } else if (length === 1) {
                                //We now only have one enterprise so allocate all to that
                                parent.animalGrazing.percentageGrazed[0].percentage = 100;
                            }
                        }
                    };

                    // Clean up associated block animal distributions.
                    const { animalDistribution = {} } = offlineBudget.budget;
                    if (enterprises.length === 1) animalDistribution.blockStockRatios = "AnimalEquivalent";

                    const blocks = (offlineBudget.budget.blocks || []).map((block) => {
                        const animals = (block.animals || []).filter((a) => !idsToBeDeleted.includes(a.enterpriseId));
                        const { crops = [], cropBlock = {} } = block;
                        //History
                        filterAnimalGrazing(cropBlock);
                        /* eslint-disable no-unused-vars */
                        for (const crop of crops) {
                            //Sown
                            filterAnimalGrazing(crop);
                            const { events = [] } = crop;
                            /* eslint-disable no-unused-vars */
                            for (const event of events.filter((e) => e.type === "Defoliation")) {
                                //Defoliation
                                filterAnimalGrazing(event);
                                if (["Winteringpadbarn", "Onpastoralblocks"].includes(event.defoliationFate) && !event.animalGrazing) {
                                    delete event.defoliationFate;
                                }
                            }
                        }
                        if (animals.length === 1 && enterprises.length === 1) {
                            delete animals[0].percenBlockPastureProd;
                        }
                        return {
                            ...block,
                            animals,
                        };
                    });

                    // Clean up associated feed supplement destinations.
                    const feedSupplements = (offlineBudget.budget.feedSupplements || []).map((supplement) => {
                        const destinations = (supplement.destinations || []).filter((d) => !idsToBeDeleted.includes(d.enterpriseId));
                        return {
                            ...supplement,
                            destinations,
                        };
                    });

                    // Clean up associated dcd applications.
                    const nitrateInhibitors = (offlineBudget.budget.nitrateInhibitors || [])
                        .map((nitrateInhibitor) => {
                            const enterpriseIds = (nitrateInhibitor.enterpriseIds || []).filter((id) => !idsToBeDeleted.includes(id));
                            return {
                                ...nitrateInhibitor,
                                enterpriseIds,
                            };
                        })
                        .filter((nitrateInhibitor) => nitrateInhibitor.enterpriseIds.length > 0);

                    // Clean up any associated outdoor pig structures
                    if (enterpriseBeingDeleted.type === "OutdoorPigs") {
                        delete offlineBudget.budget.outdoorPigEffluentSystem;
                        offlineBudget.budget.structures = (offlineBudget.budget.structures || []).filter((s) => !["OutdoorPigHuts", "OutdoorPigBarns", "OutdoorPigFarrowingVillages"].includes(s.type));
                    }

                    return {
                        ...offlineBudget,
                        isDirty: true,
                        budget: {
                            ...offlineBudget.budget,
                            enterprises,
                            blocks,
                            feedSupplements,
                            nitrateInhibitors,
                            animalDistribution,
                        },
                    };
                }),
            },
        };
    });
};

const reducer = (state = [], action) => {
    let offlineFarms = state;
    if (!Array.isArray(state)) {
        offlineFarms = Object.keys(state).map((item) => state[item]);
    }

    switch (action.type) {
        case actionTypes.DISTRIBUTIONS_SAVE:
            return _saveDistributions(offlineFarms, action.payload);

        case actionTypes.ENTERPRISE_SAVE:
            return _saveEnterprise(offlineFarms, action.payload);

        case actionTypes.ENTERPRISE_DELETE:
            return _deleteEnterprise(offlineFarms, action.payload);

        default:
            return offlineFarms;
    }
};

export default reducer;
